<template>
  <div class="revenue-statistics">
    <Spin v-if="loading" fix></Spin>

    <!-- 统计概览卡片 -->
    <Row :gutter="16" class="stats-overview">
      <Col span="6" v-for="item in overviewCards" :key="item.key">
        <Card class="stat-card">
          <div class="stat-icon" :style="{ backgroundColor: item.color }">
            <Icon :type="item.icon" size="24" color="#fff" />
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
            <div class="stat-trend" v-if="item.growth !== undefined">
              <span :class="item.growth >= 0 ? 'up' : 'down'">
                <Icon :type="item.growth >= 0 ? 'ios-arrow-up' : 'ios-arrow-down'" />
                {{ Math.abs(item.growth) }}%
              </span>
              <span class="trend-desc">较上期</span>
            </div>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 图表区域 -->
    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="16">
        <Card title="收入趋势">
          <div ref="revenueTrendChart" style="height: 350px;"></div>
        </Card>
      </Col>
      <Col span="8">
        <Card title="收入来源分布">
          <div ref="revenueSourceChart" style="height: 350px;"></div>
        </Card>
      </Col>
    </Row>

    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="12">
        <Card title="区域收入排行">
          <div ref="areaRevenueChart" style="height: 300px;"></div>
        </Card>
      </Col>
      <Col span="12">
        <Card title="同比环比分析">
          <div ref="compareChart" style="height: 300px;"></div>
        </Card>
      </Col>
    </Row>

    <!-- 收入数据表格 -->
    <Card title="收入详细统计" style="margin-top: 16px;">
      <Table :columns="tableColumns" :data="tableData" border stripe />
    </Card>
  </div>
</template>

<script>
import { getRevenueStatistics } from '@/api/statistics'
import Echarts from 'echarts'

export default {
  name: 'RevenueStatistics',
  props: {
    dateRange: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      statsData: null,
      revenueTrendChart: null,
      revenueSourceChart: null,
      areaRevenueChart: null,
      compareChart: null,
      tableColumns: [
        { title: '日期', key: 'date', width: 120 },
        { title: '订单收入', key: 'orderRevenue', width: 120, render: (h, p) => h('span', { style: { color: '#f56c6c' } }, `¥${(p.row.orderRevenue || 0).toFixed(2)}`) },
        { title: '商品收入', key: 'goodsRevenue', width: 120, render: (h, p) => h('span', { style: { color: '#67C23A' } }, `¥${(p.row.goodsRevenue || 0).toFixed(2)}`) },
        { title: '总收入', key: 'totalRevenue', width: 120, render: (h, p) => h('span', { style: { color: '#409EFF', fontWeight: 'bold' } }, `¥${(p.row.totalRevenue || 0).toFixed(2)}`) },
        { title: '订单数', key: 'orderCount', width: 100 },
        { title: '日环比', key: 'dayGrowth', width: 100, render: (h, p) => h('span', { style: { color: p.row.dayGrowth >= 0 ? '#67C23A' : '#F56C6C' } }, `${p.row.dayGrowth >= 0 ? '+' : ''}${(p.row.dayGrowth || 0).toFixed(1)}%`) }
      ],
      tableData: []
    }
  },
  computed: {
    overviewCards() {
      if (!this.statsData) return []
      const data = this.statsData
      return [
        {
          key: 'total',
          label: '总收入',
          value: `¥${this.formatMoney(data.totalRevenue || 0)}`,
          growth: data.revenueGrowth,
          icon: 'logo-yen',
          color: '#F56C6C'
        },
        {
          key: 'order',
          label: '订单收入',
          value: `¥${this.formatMoney(data.orderRevenue || 0)}`,
          growth: data.orderRevenueGrowth,
          icon: 'ios-document',
          color: '#409EFF'
        },
        {
          key: 'goods',
          label: '商品收入',
          value: `¥${this.formatMoney(data.goodsRevenue || 0)}`,
          growth: data.goodsRevenueGrowth,
          icon: 'ios-cart',
          color: '#67C23A'
        },
        {
          key: 'avg',
          label: '日均收入',
          value: `¥${this.formatMoney(data.avgDailyRevenue || 0)}`,
          growth: data.avgDailyGrowth,
          icon: 'ios-calendar',
          color: '#E6A23C'
        }
      ]
    }
  },
  watch: {
    dateRange: {
      handler() {
        this.loadData()
      },
      deep: true
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1]
        }
        
        const res = await getRevenueStatistics(params)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.statsData = res.data.data
          this.tableData = this.statsData.dailyStats || []
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$Message.error(res.data?.message || '获取收入统计数据失败')
        }
      } catch (error) {
        this.$Message.error('获取收入统计数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    initCharts() {
      this.initRevenueTrendChart()
      this.initRevenueSourceChart()
      this.initAreaRevenueChart()
      this.initCompareChart()
    },
    initRevenueTrendChart() {
      if (!this.$refs.revenueTrendChart || !this.statsData) return
      
      if (this.revenueTrendChart) {
        this.revenueTrendChart.dispose()
      }
      
      this.revenueTrendChart = Echarts.init(this.$refs.revenueTrendChart)
      const trendData = this.statsData.revenueTrend || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          formatter: (params) => {
            let result = params[0].axisValue + '<br/>'
            params.forEach(param => {
              result += `${param.marker} ${param.seriesName}: ¥${param.value.toFixed(2)}<br/>`
            })
            return result
          }
        },
        legend: {
          data: ['总收入', '订单收入', '商品收入']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: trendData.map(item => item.date)
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => `¥${value >= 10000 ? (value / 10000).toFixed(1) + '万' : value}`
          }
        },
        series: [
          {
            name: '总收入',
            type: 'line',
            smooth: true,
            data: trendData.map(item => item.total),
            itemStyle: { color: '#F56C6C' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(245,108,108,0.3)' },
                  { offset: 1, color: 'rgba(245,108,108,0.05)' }
                ]
              }
            }
          },
          {
            name: '订单收入',
            type: 'line',
            smooth: true,
            data: trendData.map(item => item.orderRevenue),
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '商品收入',
            type: 'line',
            smooth: true,
            data: trendData.map(item => item.goodsRevenue),
            itemStyle: { color: '#67C23A' }
          }
        ]
      }
      
      this.revenueTrendChart.setOption(option)
    },
    initRevenueSourceChart() {
      if (!this.$refs.revenueSourceChart || !this.statsData) return
      
      if (this.revenueSourceChart) {
        this.revenueSourceChart.dispose()
      }
      
      this.revenueSourceChart = Echarts.init(this.$refs.revenueSourceChart)
      const sourceData = this.statsData.revenueSource || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: ¥{c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 20,
          data: sourceData.map(item => item.name)
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: sourceData
          }
        ],
        color: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C']
      }
      
      this.revenueSourceChart.setOption(option)
    },
    initAreaRevenueChart() {
      if (!this.$refs.areaRevenueChart || !this.statsData) return
      
      if (this.areaRevenueChart) {
        this.areaRevenueChart.dispose()
      }
      
      this.areaRevenueChart = Echarts.init(this.$refs.areaRevenueChart)
      const areaData = this.statsData.areaRevenue || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            return `${params[0].name}<br/>收入: ¥${params[0].value.toFixed(2)}`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => `¥${value >= 10000 ? (value / 10000).toFixed(1) + '万' : value}`
          }
        },
        yAxis: {
          type: 'category',
          data: areaData.map(item => item.areaName),
          inverse: true
        },
        series: [
          {
            type: 'bar',
            data: areaData.map(item => item.revenue),
            itemStyle: {
              color: new Echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#F56C6C' },
                { offset: 1, color: '#F9A7A7' }
              ])
            }
          }
        ]
      }
      
      this.areaRevenueChart.setOption(option)
    },
    initCompareChart() {
      if (!this.$refs.compareChart || !this.statsData) return
      
      if (this.compareChart) {
        this.compareChart.dispose()
      }
      
      this.compareChart = Echarts.init(this.$refs.compareChart)
      const compareData = this.statsData.compareData || { current: 0, lastPeriod: 0, lastYear: 0 }
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            return `${params[0].name}<br/>收入: ¥${params[0].value.toFixed(2)}`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: ['本期', '上期', '去年同期']
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => `¥${value >= 10000 ? (value / 10000).toFixed(1) + '万' : value}`
          }
        },
        series: [
          {
            type: 'bar',
            data: [
              { value: compareData.current, itemStyle: { color: '#409EFF' } },
              { value: compareData.lastPeriod, itemStyle: { color: '#67C23A' } },
              { value: compareData.lastYear, itemStyle: { color: '#E6A23C' } }
            ],
            barWidth: '40%'
          }
        ]
      }
      
      this.compareChart.setOption(option)
    },
    formatMoney(value) {
      if (value >= 100000000) {
        return (value / 100000000).toFixed(2) + '亿'
      }
      if (value >= 10000) {
        return (value / 10000).toFixed(2) + '万'
      }
      return value.toFixed(2)
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', () => {
      this.revenueTrendChart?.resize()
      this.revenueSourceChart?.resize()
      this.areaRevenueChart?.resize()
      this.compareChart?.resize()
    })
  },
  beforeDestroy() {
    this.revenueTrendChart?.dispose()
    this.revenueSourceChart?.dispose()
    this.areaRevenueChart?.dispose()
    this.compareChart?.dispose()
  }
}
</script>

<style lang="less" scoped>
.revenue-statistics {
  .stats-overview {
    .stat-card {
      display: flex;
      align-items: center;
      padding: 20px;
      
      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
      }
      
      .stat-content {
        flex: 1;
        
        .stat-value {
          font-size: 24px;
          font-weight: bold;
          color: #2c3e50;
          line-height: 1.2;
        }
        
        .stat-label {
          font-size: 14px;
          color: #909399;
          margin-top: 4px;
        }
        
        .stat-trend {
          font-size: 12px;
          margin-top: 8px;
          
          .up {
            color: #67C23A;
          }
          
          .down {
            color: #F56C6C;
          }
          
          .trend-desc {
            color: #909399;
            margin-left: 4px;
          }
        }
      }
    }
  }
}
</style>

